import React, { useMemo } from 'react';
import type { FormEachNode } from '../../types/layout';
import type { FormFieldMapping } from '../../types/fieldMapping';
import { FormFieldAdapter } from './FormFieldAdapter';
import { FormRowExcludedValuesProvider, FormRowProvider } from '../../context/FormRowContext';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { useEffectProcessor } from '../../processors/effectProcessor';
import { useObserverProcessor } from '../../processors/observerProcessor';
import { useRowObserverProcessor } from '../../processors/rowObserverProcessor';
import { useArrayEffectProcessor } from '../../processors/arrayEffectProcessor';
import { prefixEffects, prefixObservers } from '../../processors/pathRewriter';
import { resolveItemTokens } from '../../resolver/tokenResolver';
import { useFormEachArray } from '../../hooks/useFormEachArray';
import { resolveAddButtonPosition } from '../../utils/resolveAddButtonPosition';
import { buildExcludedSiblingValues } from './each/buildExcludedSiblingValues';
import { FormEachAddButton } from './each/FormEachAddButton';
import { FormEachRemoveButton } from './each/FormEachRemoveButton';
import { FormEachResetButton } from './each/FormEachResetButton';
import { MatrixFieldsRoot } from './MatrixFieldsMesh';
import { useMatrixColumnCount } from '../../context/MatrixColumnCountContext';
import { mergeMatrixFieldShellPresentation } from '../../utils/shellPresentation';
import styled from 'styled-components';

/**
 * A grid cell within the inline-each row — same sizing rules as MatrixFieldShell
 * but without the placement CSS (cells flow naturally within each row grid).
 */
const InlineCell = styled.div`
  box-sizing: border-box;
  min-width: 0;
`;

const ActionCell = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: flex-end;
  padding-bottom: 4px;
`;

interface FormEachInlineRendererProps {
  node: FormEachNode;
}

export const FormEachInlineRenderer = React.memo(function FormEachInlineRenderer({
  node,
}: FormEachInlineRendererProps): React.ReactElement {
  const { form, hydrationEpochRef } = useFormEngineForm();
  const { meta, getOptionProp } = useFormEngineState();
  const {
    fields,
    handleAdd,
    handleRemove,
    showAdd,
    canRemoveRow,
    allRowValues,
    siblingExcludeFields,
  } = useFormEachArray(node);

  useArrayEffectProcessor({
    form: form as never,
    arrayEffects: node.arrayEffects,
    arrayName: node.name,
    meta,
    getOptionProp,
    hydrationEpochRef,
    itemRules: node.itemRules,
    revalidateOnPerRowSync: node.revalidateOnPerRowSync,
    revalidateTargetsOnPerRowSync: node.revalidateTargetsOnPerRowSync,
  });

  const totalColumns = useMatrixColumnCount();
  const addPosition = resolveAddButtonPosition(node);
  const showRemovePerRow = canRemoveRow();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
      {addPosition === 'top-right' && showAdd && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <FormEachAddButton node={node} onClick={handleAdd} />
        </div>
      )}

      {fields.map((item, index) => (
        <InlineEachRow
          key={item.id}
          node={node}
          index={index}
          total={fields.length}
          totalColumns={totalColumns}
          excludedValues={buildExcludedSiblingValues(allRowValues, index, siblingExcludeFields)}
          showRemove={showRemovePerRow}
          onRemove={handleRemove}
        />
      ))}

      {addPosition !== 'top-right' && showAdd && (
        <div style={{ marginTop: '8px' }}>
          <FormEachAddButton node={node} onClick={handleAdd} />
        </div>
      )}
    </div>
  );
});

interface InlineEachRowProps {
  node: FormEachNode;
  index: number;
  total: number;
  /** Total column count inherited from the parent step — used to match the parent grid exactly. */
  totalColumns: number;
  excludedValues: Record<string, unknown[]>;
  showRemove: boolean;
  onRemove: (index: number) => void;
}

const InlineEachRow = React.memo(function InlineEachRow({
  node,
  index,
  total,
  totalColumns,
  excludedValues,
  showRemove,
  onRemove,
}: InlineEachRowProps): React.ReactElement {
  const { form, programmaticWriteGuard } = useFormEngineForm();
  const { meta, getOptionProp } = useFormEngineState();
  const namePrefix = `${node.name}.${index}`;

  const rowMeta = useMemo(
    () => ({
      index,
      total,
      isFirst: index === 0,
      isLast: index === total - 1,
      documentCategory: node.documentCategory,
    }),
    [index, total, node.documentCategory],
  );

  const resolvedItemFields = useMemo(() => {
    return node.itemFields.map((field) =>
      resolveItemTokens(field, namePrefix) as FormFieldMapping,
    );
  }, [node.itemFields, namePrefix]);

  const scopedEffects = useMemo(
    () => (node.itemEffects?.length ? prefixEffects(node.itemEffects, namePrefix) : []),
    [node.itemEffects, namePrefix],
  );

  const scopedObservers = useMemo(
    () => (node.itemObservers?.length ? prefixObservers(node.itemObservers, namePrefix) : []),
    [node.itemObservers, namePrefix],
  );

  useEffectProcessor({ form: form as never, effects: scopedEffects, meta, getOptionProp, programmaticWriteGuard });
  useObserverProcessor({ form: form as never, observers: scopedObservers, meta, getOptionProp });
  useRowObserverProcessor({
    form: form as never,
    observers: node.itemRowObservers ?? [],
    rowPrefix: namePrefix,
    meta,
    getOptionProp,
  });

  // Distribute columns: remove button always takes 1 col at the end when shown.
  // Fields share the remaining columns, respecting explicit colSpan from field def.
  const availableFieldCols = showRemove ? totalColumns - 1 : totalColumns;
  const getFieldColSpan = (field: FormFieldMapping, fieldIdx: number): number => {
    if (field.colSpan != null) return field.colSpan;
    const perField = Math.floor(availableFieldCols / resolvedItemFields.length);
    const remainder = availableFieldCols % resolvedItemFields.length;
    return fieldIdx === resolvedItemFields.length - 1 ? perField + remainder : perField;
  };

  return (
    <FormRowExcludedValuesProvider value={excludedValues}>
      <FormRowProvider value={rowMeta}>
        {/*
          Use MatrixFieldsRoot — the SAME grid component as FormFieldsRenderer.
          This guarantees pixel-perfect column alignment with the fields above/below.
          Each InlineEachRow is its own single-row grid with identical column spec.
        */}
        <MatrixFieldsRoot columnCount={totalColumns}>
          {resolvedItemFields.map((field, fieldIndex) => {
            const { style: shellStyle, className: shellClassName } = mergeMatrixFieldShellPresentation({
              shellPresentation: field.shellPresentation,
            });
            return (
            <InlineCell
              key={field.name || `__inline_row_${fieldIndex}`}
              className={shellClassName}
              style={{
                ...shellStyle,
                gridColumn: `span ${getFieldColSpan(field, fieldIndex)}`,
              }}
            >
              <FormFieldAdapter field={field} namePrefix={namePrefix} />
            </InlineCell>
            );
          })}
          {(showRemove || node.resetButton != null) && (
            <ActionCell style={{ gridColumn: `${totalColumns} / span 1` }}>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {node.resetButton != null && <FormEachResetButton node={node} index={index} />}
                {showRemove && <FormEachRemoveButton node={node} index={index} onRemove={onRemove} />}
              </div>
            </ActionCell>
          )}
        </MatrixFieldsRoot>
      </FormRowProvider>
    </FormRowExcludedValuesProvider>
  );
});
